import { useState, useEffect } from "react";
import { X, ArrowUp, ArrowDown, Trash2, Plus } from "lucide-react";
import { CATEGORY_COLOURS, ICON_PICKER_ORDER, ICON_REGISTRY, resolveIcon } from "../../constants.js";

export default function CategoriesModal({ open, categories, items, onClose, onSave }) {
  const [draft, setDraft] = useState([]);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (open) {
      setDraft(JSON.parse(JSON.stringify(categories)));
      setErrors([]);
    }
  }, [open, categories]);

  if (!open) return null;

  const usageById = {};
  Object.values(items || {}).forEach((it) => {
    usageById[it.category] = (usageById[it.category] || 0) + 1;
  });

  const validate = (list) => {
    const errs = [];
    if (list.length === 0) {
      errs.push("At least one category is required");
      return errs;
    }
    const ids = new Set();
    list.forEach((c, i) => {
      if (!c.label?.trim()) errs.push(`Category ${i + 1}: label is required`);
      if (!c.id?.trim()) errs.push(`Category ${i + 1}: id is required`);
      if (ids.has(c.id)) errs.push(`Category ${i + 1}: duplicate id "${c.id}"`);
      ids.add(c.id);
    });
    return errs;
  };

  const updateCat = (i, patch) => {
    setDraft((d) => d.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  };

  const addCat = () => {
    const id = `cat_${Date.now().toString(36)}`;
    const newCat = {
      id,
      label: "",
      icon: "sparkles",
      colour: CATEGORY_COLOURS[draft.length % CATEGORY_COLOURS.length],
      sortOrder: draft.length,
    };
    setDraft((d) => [...d, newCat]);
  };

  const removeCat = (i) => {
    const cat = draft[i];
    const usage = usageById[cat.id] || 0;
    if (usage > 0) {
      window.alert(
        `Can't remove "${cat.label}" — ${usage} item${usage === 1 ? "" : "s"} still use${usage === 1 ? "s" : ""} it. Reassign or delete those items first.`
      );
      return;
    }
    if (!window.confirm(`Remove "${cat.label || `category ${i + 1}`}"?`)) return;
    setDraft((d) => d.filter((_, idx) => idx !== i));
  };

  const moveCat = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= draft.length) return;
    setDraft((d) => {
      const next = [...d];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const handleSave = () => {
    const errs = validate(draft);
    if (errs.length) { setErrors(errs); return; }
    const renumbered = draft.map((c, i) => ({ ...c, sortOrder: i }));
    onSave(renumbered);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full sm:max-w-2xl bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: "92dvh", height: "92dvh" }}
      >
        <div className="px-5 pt-5 pb-3 flex items-center justify-between border-b border-slate-100 flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
              Categories
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">Order, labels, icons, and colours</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500">
            <X size={18} />
          </button>
        </div>

        <div
          className="flex-1 min-h-0 overflow-y-auto p-5 space-y-3"
          style={{ WebkitOverflowScrolling: "touch", overscrollBehavior: "contain" }}
        >
          <p className="text-xs text-slate-500">
            Items are grouped by category in the order shown — use the arrows to reorder.
            Categories in use cannot be deleted until their items are reassigned or removed.
          </p>

          {draft.map((cat, i) => {
            const Icon = resolveIcon(cat.icon);
            const usage = usageById[cat.id] || 0;
            return (
              <div
                key={cat.id}
                className="rounded-2xl border bg-slate-50/60 p-3 space-y-2.5"
                style={{ borderColor: `${cat.colour}40` }}
              >
                <div className="flex items-center gap-2">
                  <span className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: `${cat.colour}20` }}>
                    <Icon size={16} style={{ color: cat.colour }} />
                  </span>
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
                    #{i + 1} {usage > 0 && <span className="ml-1 normal-case tracking-normal text-slate-500">· {usage} item{usage === 1 ? "" : "s"}</span>}
                  </span>
                  <div className="flex-1" />
                  <button
                    onClick={() => moveCat(i, -1)}
                    disabled={i === 0}
                    className="p-1.5 rounded-full hover:bg-white text-slate-500 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move up"
                  >
                    <ArrowUp size={14} />
                  </button>
                  <button
                    onClick={() => moveCat(i, 1)}
                    disabled={i === draft.length - 1}
                    className="p-1.5 rounded-full hover:bg-white text-slate-500 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move down"
                  >
                    <ArrowDown size={14} />
                  </button>
                  <button
                    onClick={() => removeCat(i)}
                    className="p-1.5 rounded-full hover:bg-red-50 text-red-500"
                    title={usage > 0 ? `In use by ${usage} items` : "Remove category"}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1 block">Label</label>
                  <input
                    value={cat.label}
                    onChange={(e) => updateCat(i, { label: e.target.value })}
                    placeholder="e.g. Food & Wine"
                    className="w-full px-2.5 py-2 rounded-lg border border-slate-200 focus:border-slate-900 focus:outline-none text-sm bg-white"
                  />
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1 block">Icon</label>
                  <div className="flex flex-wrap gap-1.5">
                    {ICON_PICKER_ORDER.map((iconId) => {
                      const PreviewIcon = resolveIcon(iconId);
                      const active = cat.icon === iconId;
                      return (
                        <button
                          key={iconId}
                          type="button"
                          onClick={() => updateCat(i, { icon: iconId })}
                          className="w-9 h-9 rounded-lg border flex items-center justify-center transition-all"
                          style={active
                            ? { background: cat.colour, color: "white", borderColor: cat.colour }
                            : { background: "white", color: "#475569", borderColor: "#e2e8f0" }}
                          title={ICON_REGISTRY[iconId]?.label || iconId}
                        >
                          <PreviewIcon size={15} />
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1 block">Colour</label>
                  <div className="flex flex-wrap gap-1.5">
                    {CATEGORY_COLOURS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => updateCat(i, { colour: c })}
                        className="w-7 h-7 rounded-full transition-transform hover:scale-110"
                        style={{
                          background: c,
                          outline: cat.colour === c ? `2px solid ${c}` : "none",
                          outlineOffset: cat.colour === c ? "2px" : "0",
                        }}
                        title={c}
                      />
                    ))}
                  </div>
                </div>
              </div>
            );
          })}

          <button
            onClick={addCat}
            className="w-full px-4 py-3 rounded-2xl border-2 border-dashed border-slate-300 text-slate-600 hover:border-slate-500 hover:bg-slate-50 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
          >
            <Plus size={16} /> Add a category
          </button>

          {errors.length > 0 && (
            <div className="rounded-xl bg-red-50 border border-red-200 p-3">
              <ul className="text-xs text-red-700 space-y-0.5 list-disc list-inside">
                {errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}
        </div>

        <div className="px-5 py-4 bg-slate-50 border-t border-slate-100 flex gap-2 justify-end flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-slate-700 font-medium hover:bg-slate-200 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-5 py-2 rounded-full bg-slate-900 text-white font-medium hover:bg-slate-700 transition-colors"
          >
            Save categories
          </button>
        </div>
      </div>
    </div>
  );
}
